import {
  countInputMessageWords,
  getCookie,
  getFirstTenkWords,
  sendWsMessage,
  trackAnalytics,
} from "src/pages/helpers";
import { getInhouseAnonymousId } from "src/utils/anonymousId";
import { toMarkdownList } from "src/utils/chatListContinuation";
import { collectConfirmedStorageIds, hasUnsettledAttachment, getNumFutureAttachmentsAvailable } from "src/utils/attachmentHelpers";
import {
  getAskThreadMessages,
  getPresignedUrl,
  resetThreadData,
  setAwaitingCounter,
  setAwaitingResponse,
  setChatInputMessage,
  setMessagingDisabled,
  setNewThreadMessage,
  setSentences,
  setShowConnectionErrorModal,
  setUnsupportFilePopup,
  setFileSizeExceededPopup,
  setWordCountPopup,
  addAttachment,
  updateAttachmentStatus,
  // Message reliability actions
  setPendingThreadCreation,
  updatePendingThreadStatus,
  setAwaitingThreadCreationResponse,
  setSignupAlertPopup,
  setShowDocumentLimitPopup,
  clearAllAttachments,
} from "src/store/slices/messageSlice";
import { invalidateEmailThreadCache } from "src/store/slices/emailContextSlice";
import { toast } from "react-toastify";
import applicationStore from "src/store/RootStore";
import { marketingPromptMappings, SIGNUP_POPUP_TYPES, SUPPORTED_ATTACHMENT_EXTENSIONS, TRY_PAGE_MESSAGE_CAP } from "src/utils/Constants";
import { ErrorLoggingV2 } from "src/utils/errorHelpers";
import { rememberAnonymousThreadId } from "src/utils/anonymousThreadId";
import {
  BUTTON_ROLES,
  BUTTON_TYPES,
  EVENT_NAMES,
  EVENT_PARAMETERS,
  getPageName,
  MODAL_NAMES,
} from "src/utils/EventConstants";
import { createMessageTransaction, updateTransactionStatus, PersistentStorage } from "src/utils/messageReliability";

export const handleFileChange = (event, dispatch, attachments = []) => {
  const files = Array.from(event.target.files);
  if (!files || files.length === 0) {
    // toast.error("No file selected.");
    return;
  }

  const threadData = applicationStore.getState().messageSlice.threadData;
  const messagingDisabled = threadData?.messagingDisabled;
  if (messagingDisabled) {
    toast.error("Messaging is disabled. File upload is not allowed at this time.");
    event.target.value = null;
    return;
  }

  // Check attachment budget before allowing new files
  const userMetadata = applicationStore.getState().onboarding.userMetadata;
  const currentAttachments = applicationStore.getState().messageSlice.attachments || {};
  const remaining = getNumFutureAttachmentsAvailable(threadData, userMetadata) - Object.keys(currentAttachments).length;
  if (remaining <= 0) {
    dispatch(setShowDocumentLimitPopup(true));
    event.target.value = null;
    return;
  }

  const validFiles = [];
  const invalidFiles = [];

  const filesToProcess = Array.from(files);

  filesToProcess.forEach((file) => {
    const fileExtension = file?.name?.split(".").pop().toLowerCase();
    const fileSizeInMB = file.size / (1024 * 1024); // Convert to MB

    // Check file size first (25MB limit)
    if (fileSizeInMB > 25) {
      invalidFiles.push({ file, reason: "size_exceeded" });
    } else if (SUPPORTED_ATTACHMENT_EXTENSIONS.includes(fileExtension)) {
      validFiles.push(file);
    } else {
      invalidFiles.push({ file, reason: "invalid_type" });
    }
  });

  if (invalidFiles.length > 0) {
    // Check if any files exceeded size limit
    const sizeExceededFiles = invalidFiles.filter((item) => item.reason === "size_exceeded");
    const invalidTypeFiles = invalidFiles.filter((item) => item.reason === "invalid_type");

    if (sizeExceededFiles.length > 0) {
      dispatch(setFileSizeExceededPopup(true));
    } else if (invalidTypeFiles.length > 0) {
      // Pass filename(s) and file format(s) for analytics tracking
      const filenames = invalidTypeFiles.map((item) => item.file.name).join(", ");
      const fileFormats = invalidTypeFiles
        .map((item) => item.file.name.split(".").pop()?.toLowerCase() || "unknown")
        .join(", ");
      dispatch(setUnsupportFilePopup({ show: true, filename: filenames, fileFormat: fileFormats }));
    }
  }

  if (validFiles.length > 0) {
    // Cap valid files to the remaining attachment budget
    const filesToAttach = validFiles.slice(0, remaining);
    if (filesToAttach.length < validFiles.length) {
      // Some files were dropped because of the limit — show the modal
      dispatch(setShowDocumentLimitPopup(true));
    }

    // Always use the new unified attachment system
    const newAttachments = filesToAttach.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file: file,
      name: file.name,
      storageId: null,
      percentage: 0,
    }));

    // Add attachments to Redux store
    newAttachments.forEach((attachment) => {
      dispatch(addAttachment(attachment));
      // Upload funnel (F-fe): first stage — presigned URL requested. storage_id is
      // not known yet (it comes back in the response), so key on the attachment id.
      trackAnalytics(EVENT_NAMES.UPLOAD_PRESIGNED_REQUESTED, {
        [EVENT_PARAMETERS.ATTACHMENT_ID]: attachment.id,
        [EVENT_PARAMETERS.THREAD_ID]: threadData?.id,
      });
      dispatch(getPresignedUrl(attachment.id));
    });
  } else {
    // trackAnalytics(
    //   "attach-document-no-valid-files",
    //   {
    //     anonymous_user_id: getSegmentId(),
    //     user_email: localStorage.getItem("email") || "",
    //     thread_id: threadData?.id || "",
    //     subscription_type: userMetadata?.subscription_type || "non-logged-in",
    //   },
    //   userMetadata
    // );
  }

  event.target.value = null;
};

export const submitTrimmedInputMessage = ({
  dispatch,
  inputMessage,
  setInputMessage,
  setHasStartedTyping,
  eventName,
  email,
  userMetadata,
  uploadPercentage,
  referenceDocId,
  referenceDoc,
  createMessage,
  sendMessage,
  socketRef,
  trimCount = 10000,
  buttonClickType = null, // New parameter for Phase 3 button click support
  // V3.3 email-context: ids picked in the popover; rides on the WS payload
  // alongside reference_doc_storage_ids (existing thread) or through
  // createThreadWithoutMessage (new thread).
  attachedEmailThreadIds = [],
}) => {
  var threadData;
  if (buttonClickType !== "create-draft") {
    threadData = applicationStore.getState().messageSlice.threadData;
  } else {
    threadData = {};
  }
  const queryParams = new URLSearchParams(window.location.search);

  const trimmedInputMessage = inputMessage.trim();
  if (trimmedInputMessage === "" || trimmedInputMessage.length === 0) {
    setHasStartedTyping(false);
  }
  const wordCount = countInputMessageWords(trimmedInputMessage);
  if (wordCount > trimCount) {
    const msg = getFirstTenkWords(trimmedInputMessage);

    setInputMessage(msg);
    dispatch(setWordCountPopup(true));
    return;
  }

  // INH-4677: the composer renders list lines with a cosmetic indent level and a
  // `•` glyph so list mode is visible in a plain <textarea>. Everything from here
  // down is outbound — the WS payload and the sent-message stores — so convert
  // that display form to real markdown exactly once, here. It must NOT happen
  // above this line: the word-count overflow path writes back into the composer,
  // and toMarkdownList strips one indent level per call, so a converted value
  // round-tripping through composer state would flatten the user's nesting.
  const messageForPayload = toMarkdownList(trimmedInputMessage);

  const send = async () => {
    if (window.location.pathname === "/try" && !userMetadata?.id) {
      const userMessages = threadData?.messages?.filter((msg) => msg.is_user_message === true) || [];
      if (userMessages.length >= TRY_PAGE_MESSAGE_CAP) {
        dispatch(setSignupAlertPopup({ show: true, actionType: SIGNUP_POPUP_TYPES.MESSAGE_LIMIT }));
        return;
      }
    }

    // Event 91:
    trackAnalytics(
      EVENT_NAMES.CLICKED_BUTTON,
      {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_ICON,
        [EVENT_PARAMETERS.THREAD_ID]: threadData?.id,
        [EVENT_PARAMETERS.BUTTON_TEXT]: "Arrow Up Icon",
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_CLOSE,
        [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.CHATBOX,
      },
      userMetadata,
    );

    var docIds = [];

    // Handle attachments from Redux store (unified system). An attachment is
    // "confirmed" only once its `/complete` call has returned a `storageId`
    // (carried on the status object) — not merely when its S3 bytes reach 100%.
    // Belt-and-suspenders backstop for any path that reaches submit with an
    // unconfirmed attachment (e.g. the Enter-key handler, which doesn't read
    // hasUploadingAttachments): block-and-toast rather than emit a message that
    // silently drops a still-confirming file or sends a null storageId entry.
    const storeState = applicationStore.getState();
    const attachments = storeState.messageSlice.attachments || {};
    const attachmentStatuses = storeState.messageSlice.attachmentStatuses || {};
    if (hasUnsettledAttachment(attachments, attachmentStatuses)) {
      toast.error("File still finishing upload — try again in a moment", {
        toastId: "attachment-send-blocked-unconfirmed",
      });
      return;
    }
    docIds = collectConfirmedStorageIds(attachments, attachmentStatuses);

    // Use array format for new multi-file API
    var referenceDocStorageIds = docIds.length > 0 ? docIds : null;

    var wsmessage;

    if (threadData?.messages?.length > 0) {
      // Bail out if thread_id is missing — sending add-message without it
      // causes a backend InvalidWebsocketMessagePayloadException and the
      // message would land in the unsent-retry queue with a null thread_id,
      // creating a rejection storm on every reconnection.
      if (!threadData?.id) {
        toast.error("Something went wrong. Please refresh and try again.");
        return;
      }

      dispatch(setChatInputMessage(messageForPayload));

      // Create payload based on Phase 3 specification
      const payload = {
        thread_id: threadData.id,
      };

      // Add message or button click type (validation: either message,
      // button_click_type, or reference_doc_storage_ids must be provided —
      // the backend ChatAddMessageRequest validator at agent.py:44-45 permits
      // `message=null` when reference_doc_storage_ids is present).
      //
      // We previously force-filled `payload.message = " "` for attachment-only
      // sends, which made the backend create a ChatMessage with body " " and
      // the frontend render an empty bubble next to the attachment chip in
      // chat history. Now we just omit `message` for that case so only the
      // attachment lands in history.
      if (buttonClickType) {
        payload.button_click_type = buttonClickType;
      } else if (trimmedInputMessage && trimmedInputMessage.trim() !== "") {
        payload.message = messageForPayload;
      } else if (!referenceDocStorageIds) {
        // No message, no button click, no attachments — nothing to send.
        toast.error("Please enter a message");
        return;
      }

      // Add attachments if present
      if (referenceDocStorageIds) {
        payload.reference_doc_storage_ids = referenceDocStorageIds;
      }
      // V3.3 email-context: ride email selection on add-message so the
      // backend can write session_context_attachments rows before the AI
      // trigger fires. Mirrors reference_doc_storage_ids exactly.
      if (Array.isArray(attachedEmailThreadIds) && attachedEmailThreadIds.length > 0) {
        payload.attached_email_thread_ids = attachedEmailThreadIds;
        // Re-attaching a thread means its cached shelf content may now be
        // stale (new emails could exist since it was last fetched/viewed).
        dispatch(invalidateEmailThreadCache(attachedEmailThreadIds));
      }
      wsmessage = createMessage(threadData?.thread_type, "add-message", payload);
    } else {
      // NEW TWO-STEP FLOW: First create thread WITHOUT message
      dispatch(setNewThreadMessage(messageForPayload));
      // Step 1: Create thread without message (Phase 2)
      const gptContextId = queryParams.get("gpt_context_id"); // Check for GPT context
      const createThreadResult = createThreadWithoutMessage({
        dispatch,
        gptContextId,
        createMessage,
        sendMessage,
        message: messageForPayload, // Pass message for persistence
        attachments: referenceDocStorageIds || [],
        buttonClickType: buttonClickType,
        attachedEmailThreadIds: attachedEmailThreadIds,
      });

      if (!createThreadResult.success) {
        // toast.error("Failed to create thread. Please try again.");
        return;
      }

      // Clear composer attachments — storage IDs are persisted in
      // pendingThreadCreation and PersistentStorage, so the composer
      // chips are no longer needed and would otherwise stay visible
      // until the WS round-trip completes.
      if (referenceDocStorageIds) {
        dispatch(clearAllAttachments());
      }

      // Message persistence is handled inside createThreadWithoutMessage
      // The actual message will be sent after thread creation is confirmed via WebSocket handlers
      return; // Exit here - message will be sent when thread creation is confirmed
    }

    dispatch(setAwaitingCounter(0));
    dispatch(setMessagingDisabled(true));
    // Clear composer attachments immediately — storage IDs are already captured
    // in the WS payload, so keeping them in the input area is unnecessary and
    // causes the attachment chips to visually "stick" until the WS response
    // arrives and bumps threadData.messages.length.
    if (referenceDocStorageIds) {
      dispatch(clearAllAttachments());
    }
    sendWsMessage({
      message: wsmessage,
      dispatch: dispatch,
      socketRef: socketRef,
      sendMessage: sendMessage,
      scroll: true,
      onSuccess: () => {
        dispatch(setMessagingDisabled(true));
        // console.log(successMessage);
      },
      onFailure: () => {
        dispatch(setMessagingDisabled(false));
        dispatch(setAwaitingResponse(false));
      },
    });
  };

  send();
};

export const saveDataToCookies = (source) => {
  var thread = applicationStore.getState().messageSlice.threadData;
  rememberAnonymousThreadId(thread?.id);
};

export const onsubmit = (
  dispatch,
  msg,
  storageId,
  inputMessage,
  email,
  userMetadata,
  createMessage,
  socketRef,
  sendMessage,
) => {
  const trimmedInputMessage = inputMessage.trim();
  var message = msg || trimmedInputMessage;
  if (window.location.pathname === "/home") {
    dispatch(setNewThreadMessage(message));
  }
  var wsmessage = createMessage("chat", "create-thread", {
    thread_id: null,
  });
  sendWsMessage({
    message: wsmessage,
    dispatch: dispatch,
    socketRef: socketRef,
    sendMessage: sendMessage,
    scroll: true,
    onSuccess: () => {
      dispatch(setMessagingDisabled(true));
      // console.log("thread created successfully");
    },
    onFailure: () => {
      dispatch(setMessagingDisabled(false));
      dispatch(setAwaitingResponse(false));
      dispatch(setShowConnectionErrorModal(true));
      // console.log("Failed to create thread. Please try again.");
    },
  });
};

export const triggerAIResponse = ({
  dispatch,
  threadId,
  lastMessageId,
  createMessage,
  sendMessage,
  userMetadata,
  socketRef,
  buttonClickType = null,
}) => {
  if (!threadId || !lastMessageId) {
    console.error("triggerAIResponse: Missing required parameters", { threadId, lastMessageId });
    return;
  }
  trackAnalytics(
    EVENT_NAMES.AUTO_TRIGGER,
    {
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
      [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
      [EVENT_PARAMETERS.BUTTON_TEXT]: "Send Message after Files Upload Complete",
    },
    userMetadata,
  );

  const payload = {
    thread_id: threadId,
    last_message_id: lastMessageId,
    button_click_type: buttonClickType || null,
  };

  const wsmessage = createMessage("chat", "trigger-ai-response", payload);

  sendWsMessage({
    message: wsmessage,
    dispatch: dispatch,
    socketRef: socketRef,
    sendMessage: sendMessage,
    scroll: true,
    onSuccess: () => {
      // console.log("AI response triggered successfully for thread:", threadId);
    },
    onFailure: async () => {
      console.error("Failed to trigger AI response for thread:", threadId);
      ErrorLoggingV2(
        {
          thread_id: threadId,
          last_message_id: lastMessageId,
          anonymous_user_id: await getInhouseAnonymousId(),
          subscription_type: userMetadata?.subscription_type || "non-logged-in",
          user_email: localStorage.getItem("email") || "",
        },
        "trigger-ai-response-failed",
        userMetadata,
      );
    },
  });
};

export const homePageCreateThreadFromLeads = (
  dispatch,
  queryParams,
  setPage,
  setInputMessage,
  email,
  userMetadata,
  inputMessage,
  createMessage,
  socketRef,
  sendMessage,
) => {
  if (socketRef?.current?.readyState === WebSocket.OPEN) {
    if (queryParams.get("gpt_context_id") !== null) {
      // case: gpt based chat
      var threadid = queryParams.get("gpt_context_id");
      if (threadid) {
        dispatch(getAskThreadMessages({ threadId: threadid, dispatchSource: "chatboxHelpers-gptContextId" }));
      }
    } else {
      dispatch(resetThreadData());
      dispatch(setMessagingDisabled(false));
      dispatch(setSentences(["Ask me any legal questions here", "Draft a contract that...", "Review a lease for..."]));
      var cookieFromHomepage = getCookie("userInput");
      var storageCookieFromHomepage = getCookie("storageId");
      var recentlyReadRandomAccessId = localStorage.getItem("alreadyReadRandomAccessId");
      var randomAccessCookie = getCookie("randomAccessId");
      const draftChatCookie = getCookie("draftChat");
      const marketingKey = queryParams.get("marketing_prompt");
      if (draftChatCookie) {
        // Delete cookie immediately before processing to prevent duplicate
        // dispatches when homePageCreateThreadFromLeads is called by multiple useEffects.
        // Delete with both path-only and domain-qualified variants so the cookie is
        // removed regardless of whether it was set with a domain attribute.
        const baseDomain = process.env.REACT_APP_BASE_DOMAIN || "inhouse.ai";
        document.cookie = "draftChat=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
        document.cookie = `draftChat=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${baseDomain}`;
        try {
          const draft = JSON.parse(decodeURIComponent(draftChatCookie));
          if (draft.message) {
            setInputMessage(draft.message);
          }
          if (Array.isArray(draft.attachments)) {
            draft.attachments.forEach((att) => {
              if (att.storageId) {
                const attId = att.id || `draft-${Date.now()}-${Math.random()}`;
                dispatch(addAttachment({ id: attId, name: att.name, storageId: att.storageId, file: null }));
                dispatch(updateAttachmentStatus({
                  attachmentId: attId,
                  status: { storageId: att.storageId, status: "complete", percentage: 100 },
                }));
              }
            });
          }
        } catch {
          // malformed draftChat cookie — ignore
        }
      } else if (marketingKey) {
        const mapped = marketingPromptMappings[marketingKey];
        if (mapped) {
          setInputMessage(mapped);
          trackAnalytics(EVENT_NAMES.MARKETING_PROMPT_PREFILLED, {
            [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
            marketing_prompt_key: marketingKey,
          });
          const params = new URLSearchParams(window.location.search);
          params.delete("marketing_prompt");
          window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params.toString()}` : ""));
        }
      } else if (cookieFromHomepage && randomAccessCookie !== recentlyReadRandomAccessId) {
        localStorage.setItem("alreadyReadRandomAccessId", randomAccessCookie);
        setInputMessage(cookieFromHomepage);
      } else if (queryParams.get("prompt")) {
        setInputMessage(queryParams.get("prompt"));
      }
    }
  } else {
    setTimeout(() => {
      homePageCreateThreadFromLeads(
        dispatch,
        queryParams,
        setPage,
        setInputMessage,
        email,
        userMetadata,
        inputMessage,
        createMessage,
        socketRef,
        sendMessage,
      );
    }, 1000);
  }
};

export const getUnprocessedAttachments = () => {
  var filesAttached = Object.values(applicationStore.getState().messageSlice.attachments || {});
  const attachmentStatuses = applicationStore.getState().messageSlice.attachmentStatuses || {};
  const filesUploaded = applicationStore.getState().messageSlice.filesToBeProcessed || {};
  var unprocessedAttachments = [];
  if (Object.keys(filesUploaded).length > 0) {
    unprocessedAttachments = Object.values(filesUploaded || {}).filter((att) => {
      const status = filesUploaded[att.reference_doc_storage_id];
      // Check attachments that are not complete or failed
      return (
        (!status || status.status !== "complete") &&
        status?.status !== "failed" &&
        att.reference_doc_storage_id &&
        status?.status !== "successful"
      );
    });
  } else if (Object.keys(filesAttached).length > 0) {
    unprocessedAttachments = Object.values(filesAttached || {}).filter((att) => {
      const attachedFile = attachmentStatuses[att.id];
      if (attachedFile?.percentage !== 100 && attachedFile?.percentage !== 98.1) {
        return false;
      }
      // Check attachments that are not complete or failed
      return (
        (!attachedFile || attachedFile.status !== "complete") &&
        attachedFile?.status !== "failed" &&
        att.storageId &&
        attachedFile?.status !== "successful"
      );
    });
  }
  return unprocessedAttachments;
};

export const createThread = ({
  dispatch,
  createMessage,
  sendMessage,
  intendedMessage,
  attachments = {},
  gptContextId = null,
}) => {
  // Set creation in progress
  dispatch({
    type: "messageSlice/setCreationInProgress",
    payload: true,
  });

  // Prepare attachments array from the attachments object
  const attachmentIds = [];
  if (attachments && Object.keys(attachments).length > 0) {
    Object.values(attachments).forEach((att) => {
      if (att.storageId) {
        attachmentIds.push(att.storageId);
      }
    });
  }

  // Create payload for new thread creation format
  const payload = {
    message: intendedMessage,
  };

  // Add attachments if any
  if (attachmentIds.length > 0) {
    payload.attachments = attachmentIds;
  }

  // Add GPT context if provided
  if (gptContextId) {
    payload.gpt_context_id = gptContextId;
  }

  // Create WebSocket message
  const wsMessage = createMessage("ask", "create-thread", payload);

  // Send the message
  const success = sendMessage(wsMessage);

  if (!success) {
    // Handle send failure
    dispatch({
      type: "messageSlice/setCreationInProgress",
      payload: false,
    });
    dispatch({
      type: "messageSlice/setFailedToCreateThread",
      payload: true,
    });
  }

  return wsMessage;
};

export const createThreadWithContext = ({
  dispatch,
  threadId = null,
  gptContextId = null,
  createMessage,
  sendMessage,
}) => {
  // Set creation in progress
  dispatch({
    type: "messageSlice/setCreationInProgress",
    payload: true,
  });

  // Create payload based on provided parameters
  const payload = {};

  if (threadId) {
    payload.thread_id = threadId;
  }

  if (gptContextId) {
    payload.gpt_context_id = gptContextId;
  }

  // Create WebSocket message
  const wsMessage = createMessage("ask", "create-thread", payload);

  // Send the message
  const success = sendMessage(wsMessage);

  if (!success) {
    // Handle send failure
    dispatch({
      type: "messageSlice/setCreationInProgress",
      payload: false,
    });
    dispatch({
      type: "messageSlice/setFailedToCreateThread",
      payload: true,
    });
  }

  return wsMessage;
};

// Phase 2: Create thread WITHOUT message (two-step flow)
export const createThreadWithoutMessage = ({
  dispatch,
  threadId = null,
  gptContextId = null,
  createMessage,
  sendMessage,
  message = null,
  attachments = [],
  buttonClickType = null,
  attachedEmailThreadIds = [],
}) => {
  // Clear stale thread data before creating a new thread so the previous
  // thread's state doesn't race with incoming WS events for the new thread.
  dispatch(resetThreadData());

  // Create transaction for tracking the entire process
  const transaction = createMessageTransaction(message, attachments);

  // Update transaction with thread creation attempt
  updateTransactionStatus(transaction.id, "THREAD_CREATING", {
    threadId,
    gptContextId,
    buttonClickType,
  });

  // Store in Redux for UI state tracking
  dispatch(
    setPendingThreadCreation({
      message,
      attachments,
      buttonClickType,
      transactionId: transaction.id,
      status: "creating",
    }),
  );

  // Set creation in progress
  dispatch({
    type: "messageSlice/setCreationInProgress",
    payload: true,
  });

  dispatch({
    type: "messageSlice/setFailedToCreateThread",
    payload: false,
  });

  // Store message in persistent storage for recovery
  if (message) {
    PersistentStorage.savePendingMessage({
      message,
      attachments,
      transactionId: transaction.id,
      timestamp: Date.now(),
      buttonClickType,
    });
  }

  // Create payload for thread creation WITHOUT message
  const payload = {};

  if (threadId) {
    payload.thread_id = threadId;
  }

  if (gptContextId) {
    payload.gpt_context_id = gptContextId;
  }

  // V3.3 email-context: ride email selection on create-thread so backend
  // writes session_context_attachments rows right after the new thread
  // is born, before the FE follows up with add-message + trigger.
  if (Array.isArray(attachedEmailThreadIds) && attachedEmailThreadIds.length > 0) {
    payload.attached_email_thread_ids = attachedEmailThreadIds;
    // Re-attaching a thread means its cached shelf content may now be stale
    // (new emails could exist since it was last fetched/viewed).
    dispatch(invalidateEmailThreadCache(attachedEmailThreadIds));
  }

  // Create WebSocket message for thread creation only
  const wsMessage = createMessage("chat", "create-thread", payload);

  // Send the message
  const success = sendMessage(wsMessage, true);

  if (!success) {
    // Handle send failure - update transaction and Redux state
    updateTransactionStatus(transaction.id, "THREAD_CREATION_FAILED", {
      error: "WebSocket send failed",
    });

    dispatch(
      updatePendingThreadStatus({
        status: "failed",
        error: "WebSocket send failed",
      }),
    );

    dispatch({
      type: "messageSlice/setCreationInProgress",
      payload: false,
    });
    dispatch({
      type: "messageSlice/setFailedToCreateThread",
      payload: true,
    });
  } else {
    dispatch(setAwaitingThreadCreationResponse(true));
  }

  return { success, transactionId: transaction.id };
};

